/**
 * Where this product lives, from the dashboard's point of view.
 *
 * The peer of api/src/config/urls.js on the backend. That file exists because
 * the product's address used to be sprinkled through the API as literals; this
 * one exists for the same reason on the frontend, where six different call
 * sites had each invented their own answer (REACT_APP_API_URL with three
 * different fallback ports, window.location.origin, api.defaults.baseURL, and
 * a hardcoded domain).
 *
 * The rule that matters for a fork: NEVER fall back to a hardcoded host. A
 * literal default is how a copy of the product ends up advertising the
 * original's domain in the snippets it tells tenants to paste into their site.
 * Falling back to our own origin is always either correct or obviously broken -
 * never quietly pointing at someone else's server.
 */

export const clean = (u) => String(u || '').trim().replace(/\/+$/, '');

const browserOrigin = () =>
  (typeof window !== 'undefined' && window.location ? clean(window.location.origin) : '');

/**
 * The origin the dashboard is being served from. In production the API, the
 * dashboard and the form all sit behind one nginx on one host, so this is also
 * the API origin - which is why it is a safe fallback everywhere below.
 */
export const APP_ORIGIN = browserOrigin();

/**
 * Baked in at build time: docker-compose passes PUBLIC_API_URL through as the
 * REACT_APP_API_URL build arg. If the build arg were ever missing we want the
 * current origin, not localhost - a localhost default in a deployed bundle is
 * indistinguishable from "the backend is down" to whoever is looking at it.
 */
export const API_URL = clean(process.env.REACT_APP_API_URL) || APP_ORIGIN;

export const FORM_URL = clean(process.env.REACT_APP_FORM_URL) || `${APP_ORIGIN}/form`;

/**
 * The base to render inside a copy-paste snippet.
 *
 * Prefers what the server reported (GET /api/settings/tracking returns `base`,
 * derived from the API's own config/urls.js, so a tenant on a custom tracking
 * domain gets THEIR domain back). Falls back to our origin while that request
 * is still in flight or if it failed.
 *
 * Several of these snippets render before the config arrives and have no
 * loading guard, so this fallback is on the visible path, not a theoretical
 * one - it is what a tenant sees on first paint.
 */
export function snippetBase(cfg) {
  if (cfg) {
    const fromBase = clean(cfg.base);
    if (fromBase) return fromBase;
    // Some callers only have the webhook URL to work from.
    if (cfg.webhook_url) {
      const origin = String(cfg.webhook_url).split('/api/track/')[0];
      if (origin) return clean(origin);
    }
  }
  return APP_ORIGIN;
}
